import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { hideMessage } from './actions';
import { clearSuccess } from './actions/success';
import { Box, CenteredSpinner, CustomPortal } from './commonComponents';
import { ErrorShower } from './commonComponents/ErrorShower';
import { BestInDentalNomineePopup } from './commonComponents/popups/BestInDentalNomineePopup';
import { BestInDentalWinnerPopup } from './commonComponents/popups/BestInDentalWinnerPopup';
import { UnavailableMobile } from './commonComponents/UnavailableMobile';
import { AcceptTermsPopup } from './components/AcceptTermsPopup';
import { JobsInviteConfirmationPopup } from './components/JobsInviteConfirmationPopup';
import { JobsInviteSelectionPopup } from './components/JobsInviteSelectionPopup';
import { ModalQueueProvider } from './components/ModalQueue';
import { OfficeTimePerPatientPopup } from './components/OfficeTimePerPatientPopup/OfficeTimePerPatientPopup';
import { OfficeTutorialPopupProvider } from './components/OfficeTutorialPopup';
import ParkingInfoPopup from './components/ParkingInfoPopup';
import { ReferralRewardPopup } from './components/ReferralRewardPopup';
import { SuccessPopup as SuccessWorkerClassificationPopup } from './components/WorkerClassification/components/SuccessPopup';
import { growthbook } from './growthbook';
import { useCookieYes, useInitialLoading, usePermPosthogSessionRecording, useSignUpPosthogSessionRecording, useUserSelector } from './hooks';
import { AuthenticatedRoutes } from './routes/AuthenticatedRoutes';
import { UnauthenticatedRoutes } from './routes/UnauthenticatedRoutes';
import { useReferralHandler } from './hooks/useReferral';
import { useCampaignTracker } from './hooks/useCampaignTracker';

export default function App() {
  const dispatch = useDispatch();
  const { isLoading } = useInitialLoading();
  const userLogged = useSelector((state) => state.user.user);
  const selectedChildOffice = useUserSelector();
  const message = useSelector((state) => state.message.message);
  const isMobileTablet = window.innerWidth <= 760;

  useReferralHandler();
  useCampaignTracker();

  usePermPosthogSessionRecording();
  useSignUpPosthogSessionRecording();

  useCookieYes();

  useEffect(() => {
    if (selectedChildOffice) {
      growthbook.setAttributes({
        id: selectedChildOffice?.id,
        userType: selectedChildOffice?.user_type,
        ...(selectedChildOffice?.address &&
          selectedChildOffice?.address[0] && { state: selectedChildOffice.address[0].state }),
      });
    }
  }, [selectedChildOffice]);

  const {
    text,
    title,
    route,
    params,
    isSuccess,
    isError,
    popupProps,
    action,
    buttonText,
    modalType,
  } = message;

  if (isMobileTablet) {
    return <UnavailableMobile />;
  }

  if (isLoading) {
    return <CenteredSpinner size={32} />;
  }

  if (!userLogged || !userLogged?.is_profile_complete || userLogged?.user_type !== 'DNT') {
    return (
      <Box>
        <UnauthenticatedRoutes />
        <ErrorShower />
        {text || title ? (
          <CustomPortal
            isSuccess={isSuccess}
            isError={isError}
            popupProps={popupProps}
            title={title}
            text={text}
            route={route}
            params={params}
            buttonText={buttonText}
            submitPressed={() => dispatch(hideMessage())}
          />
        ) : null}
        <SuccessWorkerClassificationPopup />
      </Box>
    );
  }

  return (
    <ModalQueueProvider>
      <OfficeTutorialPopupProvider>
        <AuthenticatedRoutes />
        {text || title ? (
          <CustomPortal
            isSuccess={isSuccess}
            isError={isError}
            popupProps={popupProps}
            title={title}
            text={text}
            route={route}
            params={params}
            buttonText={buttonText}
            dataCy={modalType === 'feedbackSuccess' ? 'feedback-success-modal' : undefined}
            rightButtonDataCy={modalType === 'feedbackSuccess' ? 'feedback-success-ok-button' : undefined}
            submitPressed={() => {
              if (action) {
                dispatch(clearSuccess(action));
                return;
              }
              dispatch(hideMessage());
            }}
          />
        ) : null}
        <ParkingInfoPopup />
        <AcceptTermsPopup />
        <SuccessWorkerClassificationPopup />
        <OfficeTimePerPatientPopup />
        <ReferralRewardPopup />
        <JobsInviteConfirmationPopup />
        <JobsInviteSelectionPopup />
        <BestInDentalNomineePopup />
        <BestInDentalWinnerPopup />
        <ErrorShower />
      </OfficeTutorialPopupProvider>
    </ModalQueueProvider>
  );
}
